import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * ShareQR — cinematic scan-to-visit block for Inside Mental Injustice.
 * Renders a crisp SVG QR code encoding the site URL with a click-to-copy
 * fallback for desktop users.
 *
 * Variants:
 *   • "card"    — full framed block (used in Support section)
 *   • "compact" — small footer version
 */
export default function ShareQR({
  url = "https://www.insidementalinjustice.com",
  label = "Inside Mental Injustice",
  caption = "Scan to open on your phone",
  variant = "card",
  size,
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy — long-press the QR to save it");
    }
  };

  if (variant === "compact") {
    const s = size || 96;
    return (
      <div className="flex items-start gap-4" data-testid="share-qr-compact">
        <button
          type="button"
          onClick={copy}
          aria-label={`Copy link: ${url}`}
          className="bg-white p-2 rounded-sm hover:ring-2 hover:ring-imi-gold transition-shadow"
        >
          <QRCodeSVG
            value={url}
            size={s}
            level="M"
            includeMargin={false}
            fgColor="#000000"
            bgColor="#ffffff"
          />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-imi-gold mb-1">Share the mission</div>
          <div className="font-serif text-imi-goldlight text-sm leading-snug">{label}</div>
          <div className="text-imi-text/40 text-[11px] mt-1 truncate">{copied ? "Link copied ✓" : caption}</div>
        </div>
      </div>
    );
  }

  const s = size || 200;
  return (
    <div
      data-testid="share-qr-card"
      className="border border-imi-gold/25 bg-imi-elevated p-6 md:p-7 flex items-center gap-6"
    >
      <button
        type="button"
        onClick={copy}
        aria-label={`Copy link: ${url}`}
        className="bg-white p-3 rounded-sm shrink-0 hover:ring-2 hover:ring-imi-gold transition-shadow"
      >
        <QRCodeSVG
          value={url}
          size={s}
          level="M"
          includeMargin={false}
          fgColor="#000000"
          bgColor="#ffffff"
        />
      </button>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.28em] text-imi-gold mb-2">Scan · Share · Donate</div>
        <div className="font-serif text-imi-goldlight text-xl md:text-2xl leading-tight">{label}</div>
        <p className="mt-3 text-imi-text/60 text-sm leading-relaxed">
          Point any phone camera at the code to open the site — perfect for events, flyers, and screen-shares.
        </p>
        <button
          type="button"
          onClick={copy}
          className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-imi-gold hover:text-imi-goldlight transition-colors"
        >
          {copied ? "Link copied ✓" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
